/* One binder for every field that writes a cart attribute.

   This lives in an asset loaded by the layout, not inside the cart snippet.
   The cart drawer re-renders itself by injecting HTML, and a <script> tag
   that arrives inside injected HTML never executes, so a binder written
   into the snippet is dead the moment the drawer updates. The listeners
   below are delegated from document, so they survive every re-render.

   Cart attributes, not line item properties. A gift is one parcel with one
   card and one recipient, so the answers belong to the order. That is also
   what lets the drawer and the product page write to the same place: admin
   shows them in one list on the order whichever route the buyer took. */

if (!window.__crkCartAttrBound) {
  window.__crkCartAttrBound = true;

  const CRK_GIFT_KEYS = ['Gift', 'Gift message', 'From', 'Backing', 'Hide prices on the invoice'];

  const crkFieldValue = (field) =>
    field.type === 'checkbox' ? (field.checked ? field.value || 'Yes' : '') : field.value;

  const crkStatus = (field, text) => {
    const root = field.closest('[data-crk-attr-root]');
    const el = root ? root.querySelector('[data-crk-attr-status]') : null;
    if (!el) return;
    el.textContent = text;
    el.hidden = text === '';
  };

  const crkSave = async (updates, field) => {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: updates }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      if (field) {
        crkStatus(field, 'Saved.');
        window.setTimeout(() => crkStatus(field, ''), 2500);
      }
    } catch (error) {
      console.error('[crk] cart attribute save failed', error);
      if (field) crkStatus(field, 'That did not save. Try again.');
    }
  };

  const crkSaveField = (field) => {
    const key = field.dataset.crkCartAttribute;
    if (!key) return;
    crkSave({ [key]: crkFieldValue(field) }, field);
  };

  /* The toggle saves itself and, when switched off, clears the rest, so a
     cart cannot keep a gift message after someone changes their mind. It
     also mirrors across surfaces: the drawer and the product page show the
     same cart, so they must never disagree about whether this is a gift. */
  const crkToggleGift = (toggle) => {
    const on = toggle.checked;

    document.querySelectorAll('[data-crk-gift-root]').forEach((root) => {
      const own = root.querySelector('[data-crk-gift-toggle]');
      if (own && own !== toggle) own.checked = on;

      const fields = root.querySelector('[data-crk-gift-fields]');
      if (fields) fields.hidden = !on;

      if (!on) {
        root.querySelectorAll('[data-crk-cart-attribute]').forEach((field) => {
          if (field.hasAttribute('data-crk-gift-toggle')) return;
          if (field.type === 'checkbox') field.checked = false;
          else field.value = '';
        });
        const counter = root.querySelector('[data-crk-gift-counter]');
        const counted = root.querySelector('[data-crk-gift-counted]');
        if (counter && counted) counter.textContent = '0 / ' + counted.maxLength;
      }
    });

    if (on) {
      crkSave({ Gift: 'Yes' }, toggle);
      const root = toggle.closest('[data-crk-gift-root]');
      const first = root
        ? root.querySelector('[data-crk-gift-fields] [data-crk-cart-attribute]')
        : null;
      if (first) first.focus();
      return;
    }

    const cleared = {};
    CRK_GIFT_KEYS.forEach((key) => {
      cleared[key] = '';
    });
    crkSave(cleared, toggle);
  };

  document.addEventListener('change', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;

    const toggle = el.closest('[data-crk-gift-toggle]');
    if (toggle) {
      crkToggleGift(toggle);
      return;
    }

    const field = el.closest('[data-crk-cart-attribute]');
    if (field) crkSaveField(field);
  });

  document.addEventListener(
    'blur',
    (event) => {
      const el = event.target instanceof Element ? event.target : null;
      const field = el
        ? el.closest('input[data-crk-cart-attribute], textarea[data-crk-cart-attribute]')
        : null;
      if (field && !field.hasAttribute('data-crk-gift-toggle')) crkSaveField(field);
    },
    true
  );

  document.addEventListener('input', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    const counted = el ? el.closest('[data-crk-gift-counted]') : null;
    if (!counted) return;
    const root = counted.closest('[data-crk-gift-root]');
    const counter = root ? root.querySelector('[data-crk-gift-counter]') : null;
    if (counter) counter.textContent = counted.value.length + ' / ' + counted.maxLength;
  });

  /* Add to cart takes about a second to answer, and for that whole second
     the button used to do nothing at all: no spinner, no disabled state,
     no change of any kind. On a phone on mobile data that second is
     longer, and a button that looks idle reads as a button that did not
     work, so people press it again and reach checkout with two of
     everything.

     This marks the button busy the moment its form is submitted and
     clears it when the cart answers. It is a pulse on the button itself,
     not a spinner set beside the label: the Dressing Room button is a
     fixed pill sized to two lines of text, and anything added inside it
     overflows the shape. A pulse adds no element and no copy, cannot
     shift the layout, and works whatever colour or size the button is.
     A confirmed cart response shows the Kit Bag notice. The listener is on document
     and in the capture phase, so one binding covers the product page,
     the Dressing Room cards and any button rendered later. */
  const CRK_ATC_CSS =
    '.crk-atc-busy{pointer-events:none;' +
    'animation:crk-atc-pulse .9s ease-in-out infinite}' +
    '@keyframes crk-atc-pulse{0%,100%{opacity:1}50%{opacity:.45}}' +
    '@media (prefers-reduced-motion:reduce){' +
    '.crk-atc-busy{animation:none;opacity:.55}}';

  const crkAtcStyle = document.createElement('style');
  crkAtcStyle.textContent = CRK_ATC_CSS;
  document.head.appendChild(crkAtcStyle);

  const crkAtcBusy = new Set();
  let crkAtcAdding = false;

  const crkAtcConfirm = () => {
    const notice = document.getElementById('crk-cart-notice');
    const message = notice?.querySelector('[data-crk-cart-message]');
    if (!notice || !message) return;
    notice.hidden = false;
    message.textContent = message.dataset.success;
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const notice = document.getElementById('crk-cart-notice');
    if (event.target.closest('[data-crk-dismiss-notice]')) {
      if (notice) notice.hidden = true;
      return;
    }
    if (!event.target.closest('[data-crk-view-bag]')) return;
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      // Open after this click finishes so the drawer backdrop handler
      // cannot treat the same click as an outside click.
      window.setTimeout(() => drawer.open(), 0);
      if (notice) notice.hidden = true;
    } else {
      window.location.assign((window.Shopify?.routes?.root || '/') + 'cart');
    }
  });

  const crkAtcClear = () => {
    crkAtcBusy.forEach((button) => {
      button.classList.remove('crk-atc-busy');
      button.removeAttribute('aria-busy');
    });
    crkAtcBusy.clear();
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

      const button = form.querySelector('button[type="submit"], button:not([type])');
      if (!button || crkAtcBusy.has(button)) return;

      button.classList.add('crk-atc-busy');
      button.setAttribute('aria-busy', 'true');

      crkAtcBusy.add(button);
      crkAtcAdding = true;

      /* A failsafe, so a request that never answers cannot leave the
         button dead for the rest of the visit. */
      window.setTimeout(crkAtcClear, 6000);
    },
    true
  );

  document.addEventListener('cart:update', (event) => {
    crkAtcClear();
    if (!crkAtcAdding) return;
    crkAtcAdding = false;
    if (event.detail?.data?.didError) return;
    crkAtcConfirm();
  });

  document.addEventListener('cart:error', () => {
    crkAtcAdding = false;
    crkAtcClear();
  });
}

